import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { makeRequest } from "../../../../services/api/client";
import { getOCELMetadata } from "../../../../services/api/ocel.api";
import { useOcelMetadata } from "../../../../stores/OcelContext";

const NUMERIC_OCEL_TYPES = new Set(["float", "integer", "int", "double", "number"]);

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const normalizeSelectedDateForGranularity = (selectedDate, granularity) => {
  if (!selectedDate || granularity === "day") return selectedDate;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedDate);
  if (!m) return selectedDate;

  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const pad2 = (n) => String(n).padStart(2, "0");

  if (granularity === "month") {
    return `${year}-${pad2(month)}-01`;
  }
  if (granularity === "year") {
    return `${year}-01-01`;
  }
  if (granularity === "week") {
    const dt = new Date(Date.UTC(year, month - 1, day));
    const mondayOffset = (dt.getUTCDay() + 6) % 7;
    dt.setUTCDate(dt.getUTCDate() - mondayOffset);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return selectedDate;
};

const useAttributeSumBarChart = (activeFile, initialSettings = null) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const configKey = `attributeSumBarChartConfig_${activeFile || "default"}`;
  const viewKey = `attributeSumBarChartView_${activeFile || "default"}`;

  // --- Data / loading state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [datasetLastDate, setDatasetLastDate] = useState(null);

  // --- Metadata state ---
  const [objectTypes, setObjectTypes] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [objectTypeNumericAttributes, setObjectTypeNumericAttributes] =
    useState({});

  const eventTypeNumericAttributes = useMemo(() => {
    if (!metadata?.event_types) return {};
    const result = {};
    for (const et of metadata.event_types) {
      result[et.name] = (et.attributes || [])
        .filter((a) => NUMERIC_OCEL_TYPES.has((a.type || "").toLowerCase()))
        .map((a) => a.name);
    }
    return result;
  }, [metadata]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // --- TileConfig (persistent, set in Edit Mode) ---
  const [title, setTitle] = useState("Attribute Sum Bar Chart");
  const [attributeSource, setAttributeSource] = useState("object"); // "object" | "event"
  const [selectedObjectTypes, setSelectedObjectTypes] = useState([]);
  const [selectedObjectAttributes, setSelectedObjectAttributes] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedEventAttributes, setSelectedEventAttributes] = useState([]);
  const [enabledGranularities, setEnabledGranularities] = useState([
    "day",
    "week",
    "month",
    "year",
  ]);

  // --- ViewState (runtime, set in View Mode) ---
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  const [viewMode, setViewMode] = useState("granularity"); // "granularity" | "daterange"
  const [dateRangeStart, setDateRangeStart] = useState(yesterdayISO());
  const [dateRangeEnd, setDateRangeEnd] = useState(yesterdayISO());

  // --- Request dedup ref ---
  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // --- Restore TileConfig from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);

      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (cfg.attributeSource === "object" || cfg.attributeSource === "event")
        setAttributeSource(cfg.attributeSource);
      if (Array.isArray(cfg.selectedObjectTypes))
        setSelectedObjectTypes(cfg.selectedObjectTypes);
      if (Array.isArray(cfg.selectedObjectAttributes))
        setSelectedObjectAttributes(cfg.selectedObjectAttributes);
      if (Array.isArray(cfg.selectedEventTypes))
        setSelectedEventTypes(cfg.selectedEventTypes);
      if (Array.isArray(cfg.selectedEventAttributes))
        setSelectedEventAttributes(cfg.selectedEventAttributes);
      if (
        Array.isArray(cfg.enabledGranularities) &&
        cfg.enabledGranularities.length > 0
      )
        setEnabledGranularities(cfg.enabledGranularities);
    } catch {
      // ignore
    }
  }, [configKey]);

  // --- Restore ViewState from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) return;
      const vs = JSON.parse(raw);

      if (typeof vs.granularity === "string") setGranularity(vs.granularity);
      if (typeof vs.selectedDate === "string") setSelectedDate(vs.selectedDate);
      if (vs.viewMode === "granularity" || vs.viewMode === "daterange")
        setViewMode(vs.viewMode);
      if (typeof vs.dateRangeStart === "string")
        setDateRangeStart(vs.dateRangeStart);
      if (typeof vs.dateRangeEnd === "string") setDateRangeEnd(vs.dateRangeEnd);
    } catch {
      // ignore
    }
  }, [viewKey]);

  // --- Restore from centralised config (overrides localStorage on mount) ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.attributeSource !== undefined) setAttributeSource(s.attributeSource);
    if (s.selectedObjectTypes !== undefined) setSelectedObjectTypes(s.selectedObjectTypes);
    if (s.selectedObjectAttributes !== undefined) setSelectedObjectAttributes(s.selectedObjectAttributes);
    if (s.selectedEventTypes !== undefined) setSelectedEventTypes(s.selectedEventTypes);
    if (s.selectedEventAttributes !== undefined) setSelectedEventAttributes(s.selectedEventAttributes);
    if (s.enabledGranularities !== undefined) setEnabledGranularities(s.enabledGranularities);
    if (s.granularity !== undefined) setGranularity(s.granularity);
    if (s.selectedDate !== undefined) setSelectedDate(s.selectedDate);
    if (s.viewMode !== undefined) setViewMode(s.viewMode);
    if (s.dateRangeStart !== undefined) setDateRangeStart(s.dateRangeStart);
    if (s.dateRangeEnd !== undefined) setDateRangeEnd(s.dateRangeEnd);
  }, []);

  // --- Persist TileConfig ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          attributeSource,
          selectedObjectTypes,
          selectedObjectAttributes,
          selectedEventTypes,
          selectedEventAttributes,
          enabledGranularities,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
  ]);

  // --- Persist ViewState ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          granularity,
          selectedDate,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    viewKey,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
  ]);

  // --- Fetch numeric attributes for selected object types (schema-based, like AggregationKPI) ---
  const fetchNumericAttributesForObjectTypes = useCallback(async (types) => {
    if (!activeFile || !types.length) return;
    try {
      const results = await Promise.all(
        types.map((type) =>
          makeRequest(
            "/ocel/object-type-attributes",
            { params: { file_type: activeFile, object_type: type } },
            "object type attributes",
          ),
        ),
      );
      setObjectTypeNumericAttributes((prev) => {
        const next = { ...prev };
        types.forEach((type, i) => {
          next[type] = results[i]?.numeric_attributes || [];
        });
        return next;
      });
    } catch {
      // ignore — keeps existing values
    }
  }, [activeFile]);

  // --- Fetch metadata ---
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const metadataRes = await api.post(
        "/kpi/attribute-sum-bar-chart/metadata",
        { file_type: activeFile },
        {},
        "Attribute Sum Metadata",
      );

      setObjectTypes(metadataRes.object_types || []);
      setEventTypes(metadataRes.event_types || []);

      if (metadataRes.object_type_numeric_attributes) {
        setObjectTypeNumericAttributes(
          metadataRes.object_type_numeric_attributes,
        );
      }

      // Fetch dataset date range and snap selectedDate if needed
      try {
        const dateMetaRes = await getOCELMetadata(activeFile);
        if (dateMetaRes?.date_range?.end) {
          const lastDate = dateMetaRes.date_range.end.slice(0, 10);
          setDatasetLastDate(lastDate);
          setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
        }
      } catch {
        // Non-critical
      }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api]);

  // --- Fetch chart data ---
  const fetchChartData = useCallback(
    async (overrides = {}) => {
      const effectiveAttributeSource =
        overrides.attributeSource ?? attributeSource;
      const effectiveSelectedObjectTypes =
        overrides.selectedObjectTypes ?? selectedObjectTypes;
      const effectiveSelectedObjectAttributes =
        overrides.selectedObjectAttributes ?? selectedObjectAttributes;
      const effectiveSelectedEventTypes =
        overrides.selectedEventTypes ?? selectedEventTypes;
      const effectiveSelectedEventAttributes =
        overrides.selectedEventAttributes ?? selectedEventAttributes;
      const effectiveGranularity = overrides.granularity ?? granularity;
      const effectiveEnabledGranularities =
        overrides.enabledGranularities ?? enabledGranularities;
      const effectiveSelectedDate = overrides.selectedDate ?? selectedDate;
      const normalizedGranularityDate = normalizeSelectedDateForGranularity(
        effectiveSelectedDate,
        effectiveGranularity,
      );
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }

      // Validate required selections
      if (effectiveAttributeSource === "object") {
        if (!effectiveSelectedObjectTypes.length) {
          setError("Please select at least one object type");
          return false;
        }
        if (!effectiveSelectedObjectAttributes.length) {
          setError("Please select at least one object attribute");
          return false;
        }
      } else {
        if (!effectiveSelectedEventTypes.length) {
          setError("Please select at least one event type");
          return false;
        }
        if (!effectiveSelectedEventAttributes.length) {
          setError("Please select at least one event attribute");
          return false;
        }
      }

      const isDateRange = effectiveViewMode === "daterange";
      if (!isDateRange && !effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }
      if (isDateRange && (!effectiveDateRangeStart || !effectiveDateRangeEnd)) {
        setError("Please select start and end dates");
        return false;
      }

      const payload = {
        file_type: activeFile,
        attribute_source: effectiveAttributeSource,
        selected_object_types: effectiveSelectedObjectTypes,
        selected_object_attributes: effectiveSelectedObjectAttributes,
        selected_event_types: effectiveSelectedEventTypes,
        selected_event_attributes: effectiveSelectedEventAttributes,
        aggregation_mode: "accumulated",
        ...(isDateRange
          ? {
              period_start: effectiveDateRangeStart,
              period_end: effectiveDateRangeEnd,
              granularity: "day",
              selected_date: effectiveDateRangeStart,
            }
          : {
              granularity: effectiveGranularity,
              selected_date: normalizedGranularityDate,
              enabled_granularities: effectiveEnabledGranularities,
            }),
      };

      const requestSignature = JSON.stringify({
        ...payload,
        selected_object_types: [
          ...(payload.selected_object_types || []),
        ].sort(),
        selected_object_attributes: [
          ...(payload.selected_object_attributes || []),
        ].sort(),
        selected_event_types: [...(payload.selected_event_types || [])].sort(),
        selected_event_attributes: [
          ...(payload.selected_event_attributes || []),
        ].sort(),
        ...(payload.enabled_granularities
          ? { enabled_granularities: [...payload.enabled_granularities].sort() }
          : {}),
      });

      if (inFlightSignatureRef.current === requestSignature) return true;
      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;
        const response = await api.post(
          "/kpi/attribute-sum-bar-chart",
          payload,
          {},
          "Attribute Sum Bar Chart",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setChartData(response);
        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setChartData(null);
        let msg = err?.message || "Failed to load chart data";
        if (msg.includes("404")) {
          msg = "Endpoint not found. Please check if the backend is running.";
        } else if (msg.includes("400")) {
          msg = "Invalid configuration. Please check your settings.";
        }
        setError(msg);
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      api,
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
      granularity,
      selectedDate,
      viewMode,
      dateRangeStart,
      dateRangeEnd,
    ],
  );

  // --- Reset on file change ---
  useEffect(() => {
    setChartData(null);
    setError(null);
    setIsMetadataLoaded(false);
    setObjectTypes([]);
    setEventTypes([]);
    setObjectTypeNumericAttributes({});
    setDatasetLastDate(null);
  }, [activeFile]);

  return {
    // Data
    loading,
    error,
    chartData,
    datasetLastDate,
    // Metadata
    objectTypes,
    eventTypes,
    objectTypeNumericAttributes,
    eventTypeNumericAttributes,
    isMetadataLoaded,
    // TileConfig
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    // ViewState
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    // TileConfig setters
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    // ViewState setters
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchNumericAttributesForObjectTypes,
  };
};

export { useAttributeSumBarChart };
